import { API_BASE_URL } from '../Utils/constants';
import { getValidIdToken } from '../Access/services/authService';
import { refreshBearerAfter401, withBearer } from './authFetch';

/**
 * Cliente HTTP compartido. Centraliza URL base, headers de autenticación, parseo
 * de la respuesta, timeout y cancelación, para que los servicios solo declaren
 * el endpoint y sus parámetros.
 *
 * Contrato: las funciones **no lanzan por errores HTTP**. Devuelven el JSON del
 * backend más los metadatos `_httpOk`, `_httpStatus` y `_httpHeaders`, y el
 * consumidor decide qué hacer. Sí propagan los errores de transporte (red caída,
 * cancelación, timeout), que son excepcionales.
 *
 * @see docs/CONVENTIONS.md sección 6
 */

const DEFAULT_TIMEOUT_MS = 20000;
const RETRY_429_DELAY_MS = 400;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const buildUrl = (path, params) => {
  const base = path.startsWith('http')
    ? path
    : `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!params) return base;

  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });
  const query = search.toString();
  return query ? `${base}?${query}` : base;
};

const buildHeaders = ({ hasJsonBody, headers, bearer }) => {
  const result = { Accept: 'application/json', ...headers };
  if (hasJsonBody) result['Content-Type'] = 'application/json';
  // Id token (claim email). El access token no sirve para allowlist type:email.
  // Se adjunta solo si hay sesión: share public acepta JWT opcional.
  return withBearer(result, bearer);
};

const parseJsonResponse = async (response) => {
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
    _httpHeaders: response.headers,
  };
};

/**
 * Combina el `AbortSignal` del consumidor con el del timeout interno, y expone
 * si el corte lo provocó el timeout para poder distinguirlo de una cancelación.
 */
const createRequestSignal = (externalSignal, timeoutMs) => {
  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = timeoutMs > 0
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : null;

  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onExternalAbort);
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    },
  };
};

/**
 * Ejecuta una request contra el backend.
 *
 * @param {string} path - Ruta relativa a `API_BASE_URL`, o URL absoluta.
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {object} [options.params] - Query string; se omiten los valores vacíos.
 * @param {object|FormData} [options.body] - Se serializa a JSON salvo que sea FormData.
 * @param {boolean} [options.auth=true] - Adjunta el Bearer si hay sesión (renovado
 *   si está por vencer) y reintenta una vez ante 401.
 * @param {object} [options.headers] - Headers extra.
 * @param {AbortSignal} [options.signal] - Cancelación del consumidor.
 * @param {number} [options.timeoutMs=20000] - 0 desactiva el timeout.
 * @param {number} [options.retryOn429=0] - Reintentos ante 429, con espera fija.
 * @param {boolean} [options.retryOn401=true] - Con Bearer, un refresh + un reintento
 *   ante 401. `false` en rutas cuyo 401 no depende del token (p. ej. `/getpmtilesurl`).
 * @returns {Promise<object>} Envelope `{ ...json, _httpOk, _httpStatus, _httpHeaders }`.
 * @throws {Error} `TimeoutError` al vencer el timeout; `AbortError` si canceló el
 *   consumidor; el error original de `fetch` ante fallo de red.
 */
export const request = async (path, options = {}) => {
  const {
    method = 'GET',
    params,
    body,
    auth = true,
    headers,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retryOn429 = 0,
    retryOn401 = true,
  } = options;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const hasJsonBody = body !== undefined && body !== null && !isFormData;
  const url = buildUrl(path, params);

  // El token se resuelve antes del loop: el timeout no corre durante el refresh.
  let bearer = auth ? await getValidIdToken() : null;
  let retried401 = false;

  const requestInit = { method };
  if (body !== undefined && body !== null) {
    requestInit.body = isFormData ? body : JSON.stringify(body);
  }

  for (let attempt = 0; ; attempt += 1) {
    const { signal: requestSignal, didTimeout, cleanup } = createRequestSignal(signal, timeoutMs);

    let response;
    try {
      response = await fetch(url, {
        ...requestInit,
        headers: buildHeaders({ hasJsonBody, headers, bearer }),
        signal: requestSignal,
      });
    } catch (error) {
      if (didTimeout()) {
        const timeoutError = new Error(`Timeout de ${timeoutMs}ms en ${method} ${url}`);
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      throw error;
    } finally {
      cleanup();
    }

    if (response.status === 429 && attempt < retryOn429) {
      await delay(RETRY_429_DELAY_MS);
      continue;
    }

    if (response.status === 401 && retryOn401 && bearer && !retried401) {
      retried401 = true;
      const nextBearer = await refreshBearerAfter401(bearer);
      if (nextBearer && !signal?.aborted) {
        bearer = nextBearer;
        continue;
      }
    }

    return parseJsonResponse(response);
  }
};

export const get = (path, options) => request(path, { ...options, method: 'GET' });
export const post = (path, options) => request(path, { ...options, method: 'POST' });
export const put = (path, options) => request(path, { ...options, method: 'PUT' });
export const patch = (path, options) => request(path, { ...options, method: 'PATCH' });
export const del = (path, options) => request(path, { ...options, method: 'DELETE' });
